import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {ContractListHeader} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/contracts/components/contract-list-header';
import {contractsDataSelector} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/contracts/services/selectors';
import {getEndDate} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/contracts/utils';

import {Group, Item} from '@pb/kit-component-group';
import {PaginationStripe} from '@pb/kit-component-pagination';
import Typography from '@pb/kit-component-typography';


import {ContractLimitsItem} from './contract-limits-item';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {PricingModel} from 'src/openapi/admin/models/PricingModel';

type Props = {
    accountId: number,
    pricingModel: PricingModel,
    disabled?: boolean,
};

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

export const ContractLimitsList = ({accountId, pricingModel, disabled}: Props) => {
    const contractsData = useSelector(contractsDataSelector);
    const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

    const contracts = useMemo(() => [
        ...(contractsData?.activeContracts || []),
        ...(contractsData?.pastContracts || []),
    ]
        .filter(contract => contract.contractInfo.pricingModel === pricingModel)
        .sort((a, b) => {
            const endA = getEndDate(a);
            const endB = getEndDate(b);
            return !endA || !endB
                ? (endA ? 1 : 0) - (endB ? 1 : 0)
                : endB.localeCompare(endA);
        }),
    [contractsData, pricingModel]);

    const onPaginationChange = useCallback((changedPagination: Pagination) => {
        setCurrentPage(changedPagination.currentPage);
        setPageSize(changedPagination.pageSize);
    }, []);

    useEffect(() => {
        setCurrentPage(DEFAULT_PAGE);
    }, [contracts]);

    if (!contracts.length) {
        return <Typography variant="body-level-2">No contracts available.</Typography>;
    }

    const startIndex = pageSize * (currentPage - 1);
    const endIndex = startIndex + pageSize;
    const pageData = contracts.slice(startIndex, endIndex);

    return (
        <Group direction="v" gap="s" style={{paddingLeft: 32}}>
            <Item>
                <ContractListHeader creditsDataAvailable={true}/>
                {pageData.map(contract => (
                    <ContractLimitsItem
                        key={contract.contractInfo.contractId ?? contract.contractInfo.contractNumber}
                        data={contract}
                        accountId={accountId}
                        disabled={disabled}
                    />
                ))}
            </Item>
            <PaginationStripe
                totalCount={contracts.length}
                currentPage={currentPage}
                pageSize={pageSize}
                onPaginationChange={onPaginationChange}
            />
        </Group>
    );
};
